import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";

export default function AdminUserFeatures() {
  const { userId } = useParams<{ userId: string }>();
  const [features, setFeatures] = useState<{ [key: string]: boolean }>({});
  const [credit, setCredit] = useState<{
    used_chf: number;
    remaining_chf: number;
    max_cost: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  // Svevana Credits
  const [scBalance, setScBalance] = useState<number | null>(null);
  const [scAdjustAmount, setScAdjustAmount] = useState("");
  const [scAdjustNote, setScAdjustNote] = useState("");
  const [scSaving, setScSaving] = useState(false);
  const [scMsg, setScMsg] = useState("");

  const loadScBalance = async () => {
    if (!userId) return;
    const { data } = await (supabase as any)
      .from("user_credits")
      .select("balance")
      .eq("user_id", userId)
      .maybeSingle();
    setScBalance((data as any)?.balance ?? 0);
  };

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      // Features laden
      const { data: active } = await supabase
        .from("user_feature_subscriptions")
        .select("feature_key")
        .eq("user_id", userId)
        .eq("is_active", true);

      const featureStates: { [key: string]: boolean } = {
        ai_quiz_game: false,
        ai_trip_planning: false,
        trip_program_poll: false,
        virtual_participants: false,
        create_trips: false,
      };
      active?.forEach((f) => {
        if (f.feature_key) featureStates[f.feature_key] = true;
      });
      setFeatures(featureStates);

      // Guthaben laden
      const { data } = await supabase.rpc("get_ai_quiz_credit", {
        target_user_id: userId,
      });
      if (data && data.length > 0) {
        const entry = data[0];
        setCredit({
          used_chf: Number(entry.used_chf),
          remaining_chf: Number(entry.remaining_chf),
          max_cost: Number(entry.max_cost_chf_per_user),
        });
      }

      await loadScBalance();
      setLoading(false);
    };

    load();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFeature = async (feature_key: string) => {
    const isActive = !!features[feature_key];
    const { error } = await supabase
      .from("user_feature_subscriptions")
      .upsert(
        { user_id: userId, feature_key, is_active: !isActive },
        { onConflict: "user_id,feature_key" }
      );
    if (error) {
      console.error("toggleFeature failed", feature_key, error.message);
      return;
    }
    setFeatures({ ...features, [feature_key]: !isActive });
  };

  const handleScAdjust = async (isAdd: boolean) => {
    if (!userId) return;
    const val = parseInt(scAdjustAmount, 10);
    if (isNaN(val) || val <= 0) {
      setScMsg("Bitte eine gültige positive Zahl eingeben.");
      return;
    }
    setScSaving(true);
    setScMsg("");
    try {
      if (isAdd) {
        const { error } = await (supabase as any).rpc("add_credits", {
          uid: userId,
          amount: val,
          tx_type: "admin_adjustment",
          tx_desc: scAdjustNote || "Admin-Gutschrift",
        });
        if (error) throw error;
        setScMsg(`✓ ${val} SC gutgeschrieben.`);
      } else {
        const { error } = await (supabase as any).rpc("deduct_credits", {
          uid: userId,
          amount: val,
          tx_desc: scAdjustNote || "Admin-Abbuchung",
        });
        if (error) throw error;
        setScMsg(`✓ ${val} SC abgebucht.`);
      }
      setScAdjustAmount("");
      setScAdjustNote("");
      await loadScBalance();
    } catch (err: any) {
      setScMsg(`Fehler: ${err.message ?? "Unbekannter Fehler"}`);
    } finally {
      setScSaving(false);
    }
  };

  if (loading) return <p className="p-6 text-slate-400">⏳ Lade Features …</p>;

  return (
    <div className="max-w-3xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-bold mb-6 text-sky-400">
        🧩 Zusatzfunktionen
      </h1>

      <div className="space-y-6">
        {/* Feature-Switch: Reisen erstellen */}
        <div className="flex items-center justify-between">
          <span className="text-slate-300">🧭 Reisen erstellen (create_trips)</span>
          <input
            type="checkbox"
            checked={!!features.create_trips}
            onChange={() => toggleFeature("create_trips")}
            className="accent-sky-500"
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-slate-300">🎯 AI-Quiz aktiv</span>
          <input
            type="checkbox"
            checked={features.ai_quiz_game}
            onChange={() => toggleFeature("ai_quiz_game")}
            className="accent-sky-500"
          />
        </div>

        {/* Feature-Switch: KI Reiseplanung */}
        <div className="flex items-center justify-between">
          <span className="text-slate-300">✨ KI Reiseplanung (ai_trip_planning)</span>
          <input
            type="checkbox"
            checked={!!features.ai_trip_planning}
            onChange={() => toggleFeature("ai_trip_planning")}
            className="accent-sky-500"
          />
        </div>

        {/* Credit-Anzeige (altes CHF-Budget) */}
        <div className="bg-[#1e2639] border border-[#2a3347] rounded-xl p-4 text-sm text-slate-300">
          <ul className="space-y-1 text-sm">
            <li className="flex justify-between gap-4">
              <span>💰 Monatsbudget:</span>
              <span className="tabular-nums text-right w-20">
                {credit?.max_cost.toFixed(2)} CHF
              </span>
            </li>
            <li className="flex justify-between gap-4">
              <span>📊 Bereits verbraucht:</span>
              <span className="tabular-nums text-right w-20">
                {credit?.used_chf.toFixed(2)} CHF
              </span>
            </li>
            <li className="flex justify-between gap-4">
              <span>✅ Verbleibend:</span>
              <span className="tabular-nums text-right w-20">
                {credit?.remaining_chf.toFixed(2)} CHF
              </span>
            </li>
          </ul>
        </div>

        {/* ── Svevana Credits ── */}
        <div className="border border-[#2a3347] bg-[#161b27] rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-100">✨ Svevana Credits</p>
            <p className="text-lg font-bold text-slate-100">
              {scBalance !== null ? `${scBalance} SC` : <span className="text-slate-500 text-sm">Wird geladen…</span>}
            </p>
          </div>

          <div className="space-y-2">
            <input
              type="number"
              min="1"
              step="1"
              placeholder="Betrag in SC (z.B. 200)"
              value={scAdjustAmount}
              onChange={(e) => setScAdjustAmount(e.target.value)}
              className="w-full p-2 rounded border border-[#2a3347] bg-[#0d1117] text-slate-100 text-sm outline-none focus:border-sky-400 placeholder:text-slate-600"
            />
            <input
              type="text"
              placeholder="Notiz (optional)"
              value={scAdjustNote}
              onChange={(e) => setScAdjustNote(e.target.value)}
              className="w-full p-2 rounded border border-[#2a3347] bg-[#0d1117] text-slate-100 text-sm outline-none focus:border-sky-400 placeholder:text-slate-600"
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleScAdjust(true)}
                disabled={scSaving}
                className="flex-1 py-2 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 transition disabled:opacity-40"
              >
                + Guthaben hinzufügen
              </button>
              <button
                onClick={() => handleScAdjust(false)}
                disabled={scSaving}
                className="flex-1 py-2 rounded bg-[#1e2639] border border-[#2a3347] text-slate-300 text-sm hover:bg-[#2a3347] transition disabled:opacity-40"
              >
                − Guthaben abziehen
              </button>
            </div>
          </div>

          {scMsg && (
            <p className={`text-xs ${scMsg.startsWith("✓") ? "text-emerald-400" : "text-red-400"}`}>
              {scMsg}
            </p>
          )}
        </div>

        {/* Feature-Switch: Umfragen */}
        <div className="flex items-center justify-between">
          <span className="text-slate-300">🗳️ Umfragen (z. B. Menüwahl) aktiv</span>
          <input
            type="checkbox"
            checked={features.trip_program_poll}
            onChange={() => toggleFeature("trip_program_poll")}
            className="accent-sky-500"
          />
        </div>
        {/* Feature-Switch: Virtuelle Teilnehmer */}
        <div className="flex items-center justify-between">
          <span className="text-slate-300">👤 Virtuelle Teilnehmer aktiv</span>
          <input
            type="checkbox"
            checked={features.virtual_participants}
            onChange={() => toggleFeature("virtual_participants")}
            className="accent-sky-500"
          />
        </div>
      </div>
    </div>
  );
}
